import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

/** Must match `onCall({ region: 'us-central1' })` in `functions/`. */
const FUNCTIONS_REGION = 'us-central1';

// Load from environment variables; fallback to dev defaults only in development
const isDev = process.env.NODE_ENV !== 'production';
const devDefaults = {
  apiKey: 'AIzaSyCTxTCkym26FEJ9Ts1UycR4TRxiGLVBiPQ',
  authDomain: 'flash-code-e5c7b.firebaseapp.com',
  projectId: 'flash-code-e5c7b',
  storageBucket: 'flash-code-e5c7b.firebasestorage.app',
  messagingSenderId: '516767196697',
  appId: '1:516767196697:web:b4fa792b271b14c70e2c88',
  measurementId: 'G-SLZP48H5XB'
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || (isDev ? devDefaults.apiKey : ''),
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || (isDev ? devDefaults.authDomain : ''),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || (isDev ? devDefaults.projectId : ''),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || (isDev ? devDefaults.storageBucket : ''),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || (isDev ? devDefaults.messagingSenderId : ''),
  appId: process.env.REACT_APP_FIREBASE_APP_ID || (isDev ? devDefaults.appId : ''),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || (isDev ? devDefaults.measurementId : '')
};

const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    console.error(`Firebase ${key} is missing. Set REACT_APP_FIREBASE_* in .env or see .env.example.`);
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

/**
 * App Check (reCAPTCHA v3) — bot/abuse mitigation for Firestore + Auth.
 *
 * Setup:
 *   1. Firebase console → App Check → Register web app with reCAPTCHA v3 provider.
 *   2. Paste the v3 SITE KEY into REACT_APP_RECAPTCHA_V3_SITE_KEY in .env.
 *   3. In Firebase console → App Check → Apis, set Firestore + Auth to "Enforced"
 *      AFTER you've verified tokens are flowing (Monitor mode first).
 *   4. Local dev: set REACT_APP_APPCHECK_DEBUG=true in .env.development (not .env —
 *      CRA bakes .env into production builds). Open DevTools after `npm start`, copy the
 *      debug token, and add it under App Check → Apps → "Manage debug tokens".
 *      Debug tokens are never enabled when NODE_ENV === 'production'.
 */
const RECAPTCHA_V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
if (typeof window !== 'undefined') {
  // Never enable the debug provider in production builds — even if the env var was
  // accidentally present at build time (e.g. REACT_APP_APPCHECK_DEBUG in a shared .env).
  if (isDev && (process.env.REACT_APP_APPCHECK_DEBUG === 'true' || !RECAPTCHA_V3_SITE_KEY)) {
    // Enables a debug token printed to the console; register it in App Check → Manage debug tokens.
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  if (RECAPTCHA_V3_SITE_KEY) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      console.warn('[Firebase] App Check init failed:', e?.message || e);
    }
  } else if (!isDev) {
    console.error('App Check site key missing — set REACT_APP_RECAPTCHA_V3_SITE_KEY in production.');
  }
}

const auth = getAuth(app);

// Configure Firestore with CORS-friendly settings
const db = getFirestore(app);

/**
 * Opt-in: set REACT_APP_USE_FIREBASE_EMULATORS=true in .env when running:
 *   firebase emulators:start --only "functions,firestore,auth"
 * Ports must match firebase.json (Firestore 8080, Auth 9099, Functions 5001).
 */
if (
  typeof window !== 'undefined' &&
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true'
) {
  const firestoreHost = process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
  const firestorePort = Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080);
  const authHost = process.env.REACT_APP_AUTH_EMULATOR_HOST || '127.0.0.1';
  const authPort = Number(process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099);
  const fnHost = process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || '127.0.0.1';
  const fnPort = Number(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || 5001);

  try {
    connectFirestoreEmulator(db, firestoreHost, firestorePort);
  } catch (e) {
    if (!String(e?.message || '').includes('already')) {
      console.warn('[Firebase] Firestore emulator:', e?.message || e);
    }
  }
  try {
    connectAuthEmulator(auth, `http://${authHost}:${authPort}`, { disableWarnings: true });
  } catch (e) {
    if (!String(e?.message || '').includes('already')) {
      console.warn('[Firebase] Auth emulator:', e?.message || e);
    }
  }
  try {
    const functions = getFunctions(app, FUNCTIONS_REGION);
    connectFunctionsEmulator(functions, fnHost, fnPort);
  } catch (e) {
    if (!String(e?.message || '').includes('already')) {
      console.warn('[Firebase] Functions emulator:', e?.message || e);
    }
  }
  console.info(`[Firebase] Emulators: Firestore ${firestoreHost}:${firestorePort}, Auth ${authPort}, Functions ${fnPort}`);
}

// Add error handling for CORS issues
const handleFirebaseError = (error) => {
  if (error.message && error.message.includes('CORS')) {
    console.warn('CORS error detected. Some Firebase features may be limited.');
    // You can add additional CORS handling here
  }
  console.error('Firebase error:', error);
};

// Add global error handler for Firebase
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message && event.reason.message.includes('CORS')) {
      console.warn('CORS error caught globally:', event.reason);
      event.preventDefault(); // Prevent the error from being logged
    }
  });
}

// Emulator block removed — see REACT_APP_USE_FIREBASE_EMULATORS above.

// Enhanced offline persistence setup with retry logic
const setupPersistence = async (maxRetries = 3) => {
  console.log('Setting up Firestore persistence...');

  // Check if we're on iOS/iPadOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Function to attempt enabling persistence
  const attemptEnablePersistence = async (attempt = 0) => {
    try {
      await enableIndexedDbPersistence(db);
      console.log('Firestore persistence enabled successfully');
      return true;
    } catch (err) {
      if (err.code === 'failed-precondition') {
        // Multiple tabs open, persistence can only be enabled in one tab at a time
        console.warn('Persistence failed: Multiple tabs open');
        return false; // Don't retry for this error
      } else if (err.code === 'unimplemented') {
        // The current browser doesn't support persistence
        console.warn('Persistence not supported by browser');
        return false; // Don't retry for this error
      } else {
        // Other errors might be temporary
        console.error(`Persistence error (attempt ${attempt + 1}/${maxRetries}):`, err);

        if (attempt < maxRetries - 1) {
          // Wait before retrying (exponential backoff)
          const delay = Math.min(100 * Math.pow(2, attempt), 2000);
          console.log(`Retrying persistence in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptEnablePersistence(attempt + 1);
        } else {
          console.error('All persistence attempts failed');
          return false;
        }
      }
    }
  };

  // Special handling for iOS devices
  if (isIOS) {
    console.log('iOS device detected, using more cautious persistence setup');

    // For iOS, we'll use a more cautious approach with shorter timeouts
    try {
      const success = await Promise.race([
        attemptEnablePersistence(0),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Persistence timeout')), 5000)
        )
      ]);

      if (!success) {
        console.warn('Falling back to memory-only persistence for iOS device');
      }
    } catch (error) {
      console.error('iOS persistence setup failed:', error);
      console.warn('Falling back to memory-only persistence for iOS device');
    }
  } else {
    // For non-iOS devices, use the normal approach
    const success = await attemptEnablePersistence(0);

    if (!success) {
      console.warn('Persistence setup failed, some offline features may be limited');
    }
  }
};

// Initialize persistence
setupPersistence().catch(error => {
  console.error('Unexpected error during persistence setup:', error);
});

export { auth, db };
export default app;
